import React, { useState, useEffect } from 'react';
import { Card } from '../../../components/ui';
import { Store, Users, DollarSign, TrendingUp, Trophy, FileText, Calendar, RefreshCw } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { fechaLocalYMD } from '../../../utils/date';

interface BackendTicket {
  id: string;
  vendedor_id: string;
  local_id: string;
  sorteo_id: string;
  total: string;
  jugadas: Array<{ estado: string }>;
}

interface BackendSorteo {
  id: string;
  nombre: string;
  estado: string;
  hora_cierre: string;
}

export const BancaDashboardPage: React.FC = () => {
  const [tickets, setTickets] = useState<BackendTicket[]>([]);
  const [sorteos, setSorteos] = useState<BackendSorteo[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = () => {
    setLoading(true);
    const hoy = fechaLocalYMD();
    Promise.all([
      fetchAPI<BackendTicket[]>(`/tickets?fecha=${hoy}`),
      fetchAPI<BackendSorteo[]>('/sorteos?estado=ABIERTO'),
    ])
      .then(([ticketsData, sorteosData]) => {
        setTickets(ticketsData);
        setSorteos(sorteosData);
      })
      .catch(err => console.error('Error cargando datos:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const ventasHoy = tickets.reduce((sum, t) => sum + parseFloat(t.total), 0);
  const ticketsHoy = tickets.length;
  const vendedoresActivos = new Set(tickets.map(t => t.vendedor_id)).size;
  const localesActivos = new Set(tickets.map(t => t.local_id)).size;
  const jugadasActivas = tickets.reduce((sum, t) => sum + t.jugadas.filter(j => j.estado === 'ACTIVA').length, 0);
  const sorteosAbiertos = sorteos.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Banca</h1>
          <p className="text-gray-500">Banca La Fortuna - Resumen general</p>
        </div>
        <button
          onClick={fetchData}
          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          title="Refrescar"
        >
          <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6" />
            </div>
            <div>
              <p className="text-blue-100 text-sm">Ventas Hoy</p>
              <p className="text-2xl font-bold">₡{ventasHoy.toLocaleString()}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6" />
            </div>
            <div>
              <p className="text-green-100 text-sm">Tickets</p>
              <p className="text-2xl font-bold">{ticketsHoy}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div>
              <p className="text-purple-100 text-sm">Jugadas Activas</p>
              <p className="text-2xl font-bold">{jugadasActivas}</p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
              <Store className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Locales Activos</p>
              <p className="text-2xl font-bold text-gray-900">{localesActivos || 0}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-success-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-success-600" />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Vendedores</p>
              <p className="text-2xl font-bold text-gray-900">{vendedoresActivos}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-warning-100 rounded-xl flex items-center justify-center">
              <Trophy className="w-6 h-6 text-warning-600" />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Promedio/Ticket</p>
              <p className="text-2xl font-bold text-gray-900">
                ₡{ticketsHoy > 0 ? Math.round(ventasHoy / ticketsHoy).toLocaleString() : '0'}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-danger-100 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-danger-600" />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Sorteos Abiertos</p>
              <p className="text-2xl font-bold text-gray-900">{sorteosAbiertos}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Sorteos abiertos */}
      <Card>
        <h3 className="font-semibold text-gray-900 mb-4">Sorteos Abiertos</h3>
        <div className="space-y-3">
          {sorteos.length === 0 && (
            <p className="text-gray-400 text-sm">{loading ? 'Cargando...' : 'Sin sorteos abiertos'}</p>
          )}
          {sorteos.map((s) => (
            <div key={s.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                <span className="font-medium">{s.nombre}</span>
              </div>
              <span className="text-sm text-gray-500">Cierre: {s.hora_cierre}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default BancaDashboardPage;
